import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, MessageSquare, Mail, Phone, Send, Loader2, Database, Bot } from "lucide-react";
import api from "../lib/api";

const CHANNEL_ICON = { email: Mail, sms: Phone, mail: Send };

function HealthBadge({ health }) {
  const active = health === "ACTIVE";
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${
        active ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-orange-500/10 text-orange-500 border-orange-500/30"
      }`}
      data-testid="channel-health"
    >
      {health}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = (status || "").toUpperCase();
  const map = {
    RUNNING: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    COMPLETED: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
    WAITING: "bg-sky-500/10 text-sky-500 border-sky-500/30",
    PAUSED: "bg-amber-500/10 text-amber-600 border-amber-500/30",
    MANUAL: "bg-orange-500/10 text-orange-500 border-orange-500/30",
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full border whitespace-nowrap ${map[s] || "bg-muted text-muted-foreground border-border"}`} data-testid="agent-status">
      {s || "IDLE"}
    </span>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3" data-testid={testid}>
      <div className="text-2xl font-bold text-primary">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

export default function CommsAgents() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const showChannels = searchParams.get("channels") === "1";

  useEffect(() => {
    document.title = "ARQITEK — Communication Agents";
    api.get("/acq/comms/overview").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  const stats = data?.stats || {};
  const agents = data?.agents || [];
  const channels = data?.channels || [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="comms-agents-page">
      <Link to="/ai-office" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="comms-back">
        <ArrowLeft className="h-4 w-4" /> AI Office
      </Link>
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">Communication Agents</span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Communication</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          The agents and channels Angela uses to reach owners — compliant email, SMS and direct mail. Separate from
          Acquisition Intelligence, which sources and enriches leads.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          {!showChannels && (<>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="comms-stats">
            <Stat label="Emails Sent" value={stats.email_sent ?? 0} testid="stat-email" />
            <Stat label="SMS Sent" value={stats.sms_sent ?? 0} testid="stat-sms" />
            <Stat label="Mail Sent" value={stats.mail_sent ?? 0} testid="stat-mail" />
            <Stat label="Replies" value={stats.replies ?? 0} testid="stat-replies" />
          </div>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Bot className="h-4 w-4" /> Communication Agents ({agents.length})
            </h2>
            {agents.map((a) => {
              const Icon = CHANNEL_ICON[a.category === "outreach" && /mail/i.test(a.title) ? "mail" : /sms/i.test(a.title) ? "sms" : /email/i.test(a.title) ? "email" : "email"] || MessageSquare;
              return (
                <div key={a.agent_id} className="rounded-xl border border-border bg-card p-4" data-testid={`comms-agent-${a.agent_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{a.name}</span>
                        <span className="text-xs text-muted-foreground">· {a.title}</span>
                        <StatusBadge status={a.status || a.default_status} />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">{a.objective}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
                        <div><span className="text-muted-foreground">Runs: </span>{a.runs}</div>
                        <div><span className="text-muted-foreground">Channel: </span>{(a.sources || []).join(", ")}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
          </>)}

          {showChannels && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Database className="h-4 w-4" /> Channels ({channels.length})
            </h2>
            {channels.map((c, i) => {
              const Icon = CHANNEL_ICON[c.channel] || MessageSquare;
              return (
                <div key={i} className="rounded-xl border border-border bg-card p-4" data-testid={`comms-channel-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{c.name}</span>
                        <HealthBadge health={c.health} />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mt-2 text-xs">
                        <div><span className="text-muted-foreground">Type: </span>{c.type}</div>
                        <div><span className="text-muted-foreground">Jurisdictions: </span>{c.jurisdictions}</div>
                        <div><span className="text-muted-foreground">Auth: </span>{c.auth}</div>
                        <div><span className="text-muted-foreground">Cost: </span>{c.cost}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
          )}
        </>
      )}
    </div>
  );
}
